import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ServiceConfigurationKeysDataService, EnvironmentServiceConfigurationDataService, TenantEnvironmentServiceConfigurationDataService } from '../service/service-configuration-keys-data.service';
import { FormGroup, FormControl, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ServiceConfigurationKeyScoped, ServiceConfigurationScope } from '../models/configuration-setting';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ConfigurationCategory } from '../models/configuration-category';
import { EnvironmentServiceDataService } from '../service/environment-service-data.service';
import { TenantEnvironmentDataService } from '../service/tenant-environment-data.service';
import { Service } from '../models/service';
import { EnvironmentService } from '../models/environment-service';
import { TenantEnvironment, Tenant } from '../models/tenant';
import { Environment } from '../models/environment';
import { UxConstants } from 'src/app/ux-constants';

@Component({
  selector: 'app-service-configuration-key-detail',
  templateUrl: './service-configuration-key-detail.component.html',
  styleUrls: ['./service-configuration-key-detail.component.scss']
})
export class ServiceConfigurationKeyDetailComponent implements OnInit {

  constructor(
    private configKeyDataService: ServiceConfigurationKeysDataService,
    private environmentServiceDataService : EnvironmentServiceDataService,
    private tenantEnvironmentDataService: TenantEnvironmentDataService,
    private environmentKeyDataService: EnvironmentServiceConfigurationDataService,
    private tenantKeyDataService: TenantEnvironmentServiceConfigurationDataService) { }

  ngOnInit(): void {
    this.editForm = this.createFormGroup();
  }

  private configKeyScoped: ServiceConfigurationKeyScoped;

  public allowEnvironmentOverride = false;
  public allowTenantOverride = false;
  public environment: Environment;
  public tenant: Tenant;
  public active = false;
  public editForm: FormGroup;
  public itemClass: string = "modal-row-element";

  @Input() public isNew = false; 
  @Input() public scope: ServiceConfigurationScope;
  @Input() public service: Service;
  @Input() public categories: ConfigurationCategory[];

  @Input() public set environmentService (es: EnvironmentService) {
    if (!es)
      return;

    this.environmentServiceDataService.getEntity(es.Id, '$expand=Environment,Service')
      .subscribe(r => {
        this.service = r.Service;
        this.environment = r.Environment;
      })
  }

  @Input() public set tenantEnvironment (te: TenantEnvironment) {
    if (!te)
      return;

    this.tenantEnvironmentDataService.getEntity(te.Id, '$expand=Tenant')
      .subscribe(r => {
        this.tenant = r.Tenant;
      })
  }

  @Input() public set model(configKey: ServiceConfigurationKeyScoped) {
      this.configKeyScoped = configKey;
      this.editForm = this.createFormGroup();

      if (configKey) {
        this.allowEnvironmentOverride = configKey.AllowEnvironmentOverride;
        this.allowTenantOverride = configKey.AllowTenantOverride;
      }

      if (configKey && configKey.IsSecret === true) {
        if (this.scope === ServiceConfigurationScope.Service) {
          this.configKeyDataService.getSecret(configKey.Id)
          .subscribe(r => {
            this.postLoad(configKey, r.value);      
          });
        } else if (this.scope === ServiceConfigurationScope.Environment) {
          if (configKey.EnvironmentServiceConfigurationId > 0) {
            this.environmentKeyDataService.getSecret(configKey.EnvironmentServiceConfigurationId)
            .subscribe(r => {
              this.postLoad(configKey, r.value);        
            });
          } else {
            this.postLoad(configKey, null);
          }
        } else if (this.scope === ServiceConfigurationScope.Tenant) {
          if (configKey.TenantEnvironmentServiceConfigurationId > 0) {
            this.tenantKeyDataService.getSecret(configKey.TenantEnvironmentServiceConfigurationId)
            .subscribe(r => {
              this.postLoad(configKey, r.value);       
            });
          } else {
            this.postLoad(configKey, null);
          }
        }
      } else if (configKey && configKey.IsSecret === false) {
          this.postLoad(configKey, null);
      }
  }

  @Output() cancel: EventEmitter<any> = new EventEmitter();
  @Output() save: EventEmitter<ServiceConfigurationKeyScoped> = new EventEmitter();

  private postLoad(configKey: ServiceConfigurationKeyScoped, secret: string) {
    if (!configKey)
      return;

    if (configKey.IsSecret) {
      let serviceValue = this.scope == ServiceConfigurationScope.Service ? '' : UxConstants.secretMask;
      let envServiceValue = this.scope == ServiceConfigurationScope.Environment ? '' : UxConstants.secretMask;
      let tenantEnvServiceValue = this.scope == ServiceConfigurationScope.Tenant ? '' : UxConstants.secretMask;

      if (secret && secret.length > 0) {
        if (this.scope == ServiceConfigurationScope.Service) {
          serviceValue = secret;
        } else if (this.scope == ServiceConfigurationScope.Environment) {
          envServiceValue = secret;
        } else if (this.scope == ServiceConfigurationScope.Tenant) {
          tenantEnvServiceValue = secret;
        }
      }

      configKey.Value = serviceValue;
      configKey.EnvironmentValue = envServiceValue;
      configKey.TenantValue = tenantEnvServiceValue;
    }

    this.editForm.reset(configKey);  

    if (configKey.IsLocked || this.scope !== ServiceConfigurationScope.Service) {
      this.editForm.controls.CategoryId.disable();
      this.editForm.controls.Name.disable();
      this.editForm.controls.IsSecret.disable();
      this.editForm.controls.AllowEnvironmentOverride.disable();
      this.editForm.controls.AllowTenantOverride.disable();
      this.editForm.controls.Description.disable();
    }

    if (this.scope != ServiceConfigurationScope.Service) {
      this.editForm.controls.Value.disable();
    }

    if (this.scope != ServiceConfigurationScope.Environment || !configKey.AllowEnvironmentOverride) {
      this.editForm.controls.EnvironmentValue.disable();
    }

    if (this.scope != ServiceConfigurationScope.Tenant || !configKey.AllowTenantOverride) {
      this.editForm.controls.TenantValue.disable();
    }

    this.active = configKey !== undefined; 
  }

  private createFormGroup() : FormGroup {
    return new FormGroup({
      // service
      Id: new FormControl(0),
      IsLocked: new FormControl({value: '', disabled: true}),
      ServiceId: new FormControl(),
      CategoryId: new FormControl(),
      Name: new FormControl('', {updateOn: 'blur', validators: [Validators.required, Validators.minLength(3), Validators.maxLength(50), Validators.pattern('^[0-9a-zA-Z\.:_\-]*$')], asyncValidators: [this.UniqueName.bind(this)]}),
      Value: new FormControl('', [Validators.maxLength(12000)]),
      AllowTenantOverride: new FormControl(),
      AllowEnvironmentOverride: new FormControl(),
      Description: new FormControl('', [Validators.maxLength(12000), Validators.pattern('^[0-9a-zA-Z\r\n \.:;_\-]*$')]),
      IsSecret: new FormControl(),
      // environment
      EnvironmentValue: new FormControl('', [Validators.maxLength(12000)]),
      EnvironmentServiceConfigurationId: new FormControl(),
      // tenant
      TenantValue: new FormControl('', [Validators.maxLength(12000)]),
      TenantEnvironmentServiceConfigurationId: new FormControl()
    });
  }

  public onSave(e): void {
      e.preventDefault();
      let cfgKey : ServiceConfigurationKeyScoped = this.editForm.value;
      
      if (!cfgKey.Id)
        cfgKey.Id = 0;

      if (!cfgKey.EnvironmentServiceConfigurationId)
        cfgKey.EnvironmentServiceConfigurationId = 0;

      if (!cfgKey.TenantEnvironmentServiceConfigurationId)
        cfgKey.TenantEnvironmentServiceConfigurationId = 0;

      if (this.editForm.controls.CategoryId.disabled)
        cfgKey.CategoryId = this.configKeyScoped.CategoryId;

      if (this.editForm.controls.Name.disabled)
        cfgKey.Name = this.configKeyScoped.Name;

      if (this.editForm.controls.IsSecret.disabled)
        cfgKey.IsSecret = this.configKeyScoped.IsSecret;
      if (cfgKey.IsSecret === undefined || cfgKey.IsSecret === null) {
        cfgKey.IsSecret = false;
      }

      if (this.editForm.controls.AllowEnvironmentOverride.disabled)
        cfgKey.AllowEnvironmentOverride = this.configKeyScoped.AllowEnvironmentOverride;
      if (cfgKey.AllowEnvironmentOverride === undefined || cfgKey.AllowEnvironmentOverride === null) {
        cfgKey.AllowEnvironmentOverride = false;
      }

      if (this.editForm.controls.AllowTenantOverride.disabled)
        cfgKey.AllowTenantOverride = this.configKeyScoped.AllowTenantOverride;  
      if (cfgKey.AllowTenantOverride === undefined || cfgKey.AllowTenantOverride === null) {
        cfgKey.AllowTenantOverride = false;
      }

      if (this.editForm.controls.Description.disabled)
        cfgKey.Description = this.configKeyScoped.Description;

      this.save.emit(cfgKey);
      this.active = false;
  }

  public onCancel(e): void {
      e.preventDefault();
      this.closeForm();
  }

  public closeForm(): void {
      this.active = false;
      this.cancel.emit();
  }

  UniqueName(ctl: AbstractControl) : Observable<ValidationErrors | null> {
    if (ctl.pristine)
      return of(null);

    let idCtl = ctl.parent.controls['Id'];
    let serviceIdCtl = ctl.parent.controls['ServiceId'];
    let categoryIdCtl = ctl.parent.controls['CategoryId'];
    let categoryId = +categoryIdCtl.value;
    if (categoryId === -1)
      categoryId = null;

    return this.configKeyDataService.keyCount(+serviceIdCtl.value, categoryId, +idCtl.value, ctl.value)
    .pipe(
      map(
        r => 
        +r["@odata.count"] > 0 ? { unique: true } : null),
      catchError(() => of(null))
    );
  }  
}
